import { gql } from '@apollo/client';
import DetailSection from '../DetailSection';
import { idStatusTypes } from '../../../../../../constants/statusConstants';
import SummarySection from './SummarySection';
import { Alert, Flex, Text } from '@zillow/constellation';
import PhoneNumber from '../../../../../../components/PhoneNumber';
import { getTitleCase } from '../../../../../../utils/formatLogicUtil';
import TagType from '../../../../../../constants/tagType';
import { ErrorBoundary } from 'react-error-boundary';
import { CardSpinner } from '../../../../../../components/CardSpinner';
import useQueryVariables from '../../../../../../hooks/useQueryVariables';
import { useZgGraphqlQuery } from '../../../../../../hooks/useGraphqlQuery';
import { onQueryError } from '../utils/onQueryError';
import { getGlobalLogger } from '@zg-rentals/logger-base';

const logger = getGlobalLogger(__filename);

export const QUERY = gql`
  query ApplicantSummary($propertyId: ID!, $applicationId: ID!, $applicantId: ID!, $zgAuthZone: ZgAuthZone!) {
    rentalListing(id: $propertyId) {
      id
      landlordRentalApplication(id: $applicationId) {
        id
        applicant(userId: $applicantId, zgAuthZone: $zgAuthZone) {
          id
          userId
          zgAuthZone
          answers {
            id
            firstName
            middleName
            lastName
            email
            phone
            reasonForMoving
            customAnswers {
              id
              ... on BooleanAnswer {
                value
              }
            }
          }
          userStatuses {
            type
            status
          }
        }
      }
    }
  }
`;

export const ERROR_MSG = 'Sorry, there was an error loading details about the applicant';
export const MOBILITY_IMPAIRED_QUESTION_TITLE =
  'Are you mobility impaired, or do you otherwise require disability accommodation?';
export const MOBILITY_IMPAIRED_QUESTION_ID = 'mobilityImpaired';

export default function Summary() {
  const queryVariables = useQueryVariables();

  const { isError, error, isLoading, data } = useZgGraphqlQuery(QUERY, queryVariables);

  if (isError) {
    onQueryError(error);
  }

  const answers = data?.rentalListing?.landlordRentalApplication?.applicant?.answers;
  const name =
    [answers?.firstName, answers?.middleName, answers?.lastName].filter((value) => Boolean(value)).join(' ') ||
    'Summary';
  const heading = getTitleCase(name);

  const userStatuses = data?.rentalListing?.landlordRentalApplication?.applicant?.userStatuses;
  const idVerificationStatus = userStatuses?.find(({ type }) => type === 'idverification')?.status;
  let tagType = null;
  if (
    idVerificationStatus === idStatusTypes.CURRENTLY_IDENTIFIED ||
    idVerificationStatus === idStatusTypes.PREVIOUSLY_IDENTIFIED
  ) {
    tagType = TagType.idVerified;
  }

  if (isLoading)
    return (
      <DetailSection heading={heading}>
        <CardSpinner />
      </DetailSection>
    );
  if (isError) return <Alert>{ERROR_MSG}</Alert>;

  const {
    rentalListing: {
      landlordRentalApplication: {
        applicant: {
          answers: { email, phone, reasonForMoving, customAnswers },
        },
      },
    },
  } = data;

  const isMobilityImpaired = customAnswers?.find(({ id }) => id === MOBILITY_IMPAIRED_QUESTION_ID)?.value ?? null;
  const isMobilityImpairedText = isMobilityImpaired ? 'Yes' : 'No';

  return (
    <DetailSection id="applicant-summary" isMain heading={heading} tagType={tagType}>
      <ErrorBoundary fallback={null} onError={(error) => logger.warn(error)}>
        <Flex display="flex" flexDirection="column" mediaQuery={{ lg: { flexDirection: 'row' } }}>
          <Flex flex="2" mediaQuery={{ lg: { marginRight: 'md' } }} flexBasis="auto">
            {reasonForMoving && (
              <SummarySection header="Reason for moving" id="reason-for-moving">
                {`"${reasonForMoving}"`}
              </SummarySection>
            )}
            {isMobilityImpaired !== null && (
              <SummarySection header={MOBILITY_IMPAIRED_QUESTION_TITLE} id="is-mobility-impaired">
                {`"${isMobilityImpairedText}"`}
              </SummarySection>
            )}
          </Flex>
          <Flex flex="1" flexBasis="auto">
            {email && (
              <SummarySection header="Email" id="email">
                <Text>{email}</Text>
              </SummarySection>
            )}
            {phone && (
              <SummarySection header="Phone" id="phone">
                <PhoneNumber phone={phone} />
              </SummarySection>
            )}
          </Flex>
        </Flex>
      </ErrorBoundary>
    </DetailSection>
  );
}
